/**
 * Purchase Transactions API
 *
 * Reads the unified PR → PO → invoice rows for the Purchases tab from
 * `GET /projects/{projectCode}/purchase-transactions`. The backend picks FinOps or
 * FocalPoint from the company's purchasing product, resolves the instance ids from
 * `companyId`, excludes posting types 4/8, negates credits and enriches the rows
 * with Financials POs and invoices, so the rows only need light normalisation here.
 */

import type { PurchaseRequest } from '@/types/purchase-request';
import { getApiBaseUrl } from './data-service';
import { apiFetch } from './api-error-handler';
import { toIsoDateStringSafe } from './date-utils';

export type PurchaseTransactionSource = 'FinOps' | 'FocalPoint' | 'Financials';

/** Wire shape of `PurchaseTransactionDto` (backend/Models/PurchaseTransactions). */
export interface PurchaseTransactionDto {
  id: string;
  requestId: string;
  description: string;
  status: string;
  amount: number;
  net?: number | null;
  vat?: number | null;
  gross?: number | null;
  supplier: string;
  supplierName?: string | null;
  date: string;
  projectId: string;
  requestedBy: string;
  approvedBy?: string | null;
  approvedDate?: string | null;
  category: string;
  notes?: string | null;
  currency: string;
  poNumber?: string[] | null;
  invoiceNumbers?: string[] | null;
  isCommitmentRelieved?: boolean;
  costCentreCode?: string | null;
  finOpsPrimaryKey?: number | null;
  splitLine?: boolean | null;
  source: PurchaseTransactionSource;
  deepLinks?: {
    finOps?: string | null;
    focalPoint?: string | null;
    financialsPO?: string | null;
    financialsInvoice?: string | null;
  } | null;
}

const orUndefined = <T>(value: T | null | undefined): T | undefined => value ?? undefined;

export function mapPurchaseTransactionToPurchaseRequest(dto: PurchaseTransactionDto): PurchaseRequest {
  return {
    ...dto,
    status: dto.status || 'Pending',
    net: orUndefined(dto.net),
    vat: orUndefined(dto.vat),
    gross: orUndefined(dto.gross),
    supplierName: orUndefined(dto.supplierName),
    date: toIsoDateStringSafe(dto.date) ?? '',
    approvedBy: orUndefined(dto.approvedBy),
    approvedDate: toIsoDateStringSafe(dto.approvedDate),
    notes: orUndefined(dto.notes),
    currency: dto.currency || 'GBP',
    // The endpoint does not return documents yet, so live rows have none.
    documents: [],
    poNumber: dto.poNumber ?? [],
    invoiceNumbers: dto.invoiceNumbers ?? [],
    isCommitmentRelieved: dto.isCommitmentRelieved ?? false,
    costCentreCode: orUndefined(dto.costCentreCode),
    finOpsPrimaryKey: orUndefined(dto.finOpsPrimaryKey),
    splitLine: orUndefined(dto.splitLine),
    deepLinks: {
      finOps: dto.deepLinks?.finOps ?? null,
      focalPoint: dto.deepLinks?.focalPoint ?? null,
      financialsPO: dto.deepLinks?.financialsPO ?? null,
      financialsInvoice: dto.deepLinks?.financialsInvoice ?? null,
    },
  };
}

/**
 * Fetch the Purchases tab rows for a project.
 * Routes through: Frontend -> Backend -> FinOps/FocalPoint + Financials
 *
 * @param projectCode - The project code to fetch rows for
 * @param projectId - The internal project ID, echoed back on each row
 * @param companyId - The company whose purchasing and finance instances are used
 */
export async function fetchPurchaseTransactions(
  projectCode: string,
  projectId: number,
  companyId: number
): Promise<PurchaseRequest[]> {
  // Note: getApiBaseUrl() already includes /api
  const url = `${getApiBaseUrl()}/projects/${encodeURIComponent(projectCode)}/purchase-transactions`
    + `?companyId=${encodeURIComponent(String(companyId))}&projectId=${encodeURIComponent(String(projectId))}`;

  const data = await apiFetch<PurchaseTransactionDto[]>(url, { method: 'GET' });

  return Array.isArray(data) ? data.map(mapPurchaseTransactionToPurchaseRequest) : [];
}
